import { useKioskNavigation, useOrientation } from '@/hooks';
import { PATHS } from '@/router/paths';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useConfigStore } from '@/stores';
import type { GNBItemType } from '@/types';
import { processSvgData } from '@/utils/svg';
import { clsx } from 'clsx';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './GNBLayout.module.scss';
import { BuildingMenuType } from '@/enums';

interface GNBItemProps {
    item: GNBItemType;
    index: number;
    isActive: boolean;
    isFocused: boolean;
}

export const GNBItem: React.FC<GNBItemProps> = ({ item, isActive, isFocused }) => {
    const navigate = useNavigate();
    const { isHorizontal } = useOrientation();
    const { triggerFocusContent } = useKioskNavigation();
    const buttonRef = useRef<HTMLButtonElement>(null);
    // const isFAndBPage = item.route === PATHS.FNB.INDEX;
    const isFAndBPage = true;
    const contrast = useConfigStore((state) => state.config?.ui?.contrast);
    const screenMode = useConfigStore((state) => state.config?.ui?.screenMode);
    const companyTheme = useConfigStore((state) => state.config?.server?.store?.companyTheme);

    const buttonClassName = clsx('focus-border', styles['gnb-menu__menu-item'], {
        [styles['gnb-menu__menu-item--active']]: isActive,
    });

    const handleClick = useCallback(() => {
        if (item.route) {
            // log menu click (pass target route to resolve menuId)
            if (item.route !== PATHS.HOME.INDEX) {
                TrackingService.trackMenuByRoute(item.route, item.id ?? undefined);
            }
            navigate(item.route);

            // Trigger focus to content after navigation (same as Enter key behavior)
            triggerFocusContent();
        }
    }, [item, navigate, triggerFocusContent]);

    const svgColor = useMemo(() => {
        if (isActive) {
            return contrast
                ? companyTheme?.menu_button_font_color_hc || '#222222'
                : companyTheme?.menu_button_font_color || '#FFFFFF';
        }
        return contrast ? '#FFFFFF' : '#222222';
    }, [isActive, contrast, companyTheme]);

    const svgUrl = useMemo(() => {
        if (typeof item.icon !== 'string') return undefined;
        return processSvgData(item.icon, svgColor);
    }, [item.icon, svgColor]);

    // Handle scroll when focused
    useEffect(() => {
        if (isFocused && buttonRef.current) {
            // Force scroll to center the element
            buttonRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center',
            });
        }
    }, [isFocused]);

    return (
        <button
            ref={buttonRef}
            id={item?.id || ''}
            type="button"
            role="tab"
            className={buttonClassName}
            onClick={handleClick}
            aria-label={item.ariaLabel ?? item.label ?? undefined}
            aria-checked={isActive}
            data-interactive={true}
            data-tts-scope="gnb-menu-item"
            onMouseDown={(e) => e.preventDefault()}
        >
            {item.type === BuildingMenuType.HOME && item.imageUrl ? (
                <img src={item.imageUrl} alt="" className={styles['gnb-menu__image']} />
            ) : (
                <>
                    {item.icon && !(screenMode === 'split' && !isHorizontal) && (
                        <span className={styles['gnb-menu__icon']} aria-hidden="true">
                            {typeof item.icon === 'string' ? <img src={svgUrl || undefined} alt="" /> : item.icon}
                        </span>
                    )}
                    <span className={clsx(styles['gnb-menu__label'], isFAndBPage ? 'font-number' : '')}>{item.label}</span>
                </>
            )}
        </button>
    );
};
